import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Sparkles, Copy, Check, HelpCircle, Loader2, PenLine, History, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { draftsAPI } from '../lib/api';

// Panel derecho del detalle de borrador — Asistente de Redacción con IA.
// Principio central: "Tú haces tu Portafolio. La IA te ayuda a redactarlo mejor."
// La IA NUNCA reemplaza automáticamente el borrador: el docente decide.

const FUENTE_LABEL = {
    competente: 'Versión Competente (IA)',
    destacada: 'Versión Destacada (IA)',
    restauracion: 'Versión restaurada desde historial',
    ia: 'Propuesta IA',
};

function fmtFechaHora(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
}

export default function RedaccionIAPanel({ draft, onDraftUpdated, onRedaccionUpdated }) {
    const [tab, setTab] = useState('competente');
    const [respuesta, setRespuesta] = useState('');
    const [updating, setUpdating] = useState(false);
    const [applying, setApplying] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState(null); // 'competente' | 'destacada' | índice historial
    const [copied, setCopied] = useState(null);

    const redaccion = draft?.redaccion_ia || null;
    const preguntas = redaccion?.preguntas_faltantes || [];
    const historial = [...(draft?.version_history || [])].reverse();

    const textoActual = tab === 'competente'
        ? (redaccion?.version_competente || '')
        : (redaccion?.version_destacada || '');

    const handleCopiar = async (version) => {
        const texto = version === 'competente' ? redaccion?.version_competente : redaccion?.version_destacada;
        if (!texto) return;
        try {
            await navigator.clipboard.writeText(texto);
            setCopied(version);
            setTimeout(() => setCopied(null), 2000);
            toast.success('Versión copiada al portapapeles');
        } catch {
            toast.error('No se pudo copiar. Selecciona el texto manualmente.');
        }
    };

    const handleUsarVersion = async (version) => {
        const texto = version === 'competente' ? redaccion?.version_competente : redaccion?.version_destacada;
        if (!texto) return;
        setApplying(true);
        try {
            const r = await draftsAPI.applyWritingVersion(draft.id, { contenido: texto, fuente: version });
            onDraftUpdated(r.data);
            setConfirmTarget(null);
            toast.success('Propuesta aplicada a "Mi tarea". Tu versión anterior quedó guardada en el historial.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo aplicar la versión');
        } finally {
            setApplying(false);
        }
    };

    const handleRestaurar = async (v) => {
        setApplying(true);
        try {
            const r = await draftsAPI.applyWritingVersion(draft.id, { contenido: v.contenido, fuente: 'restauracion' });
            onDraftUpdated(r.data);
            setConfirmTarget(null);
            toast.success('Versión restaurada en "Mi tarea".');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo restaurar la versión');
        } finally {
            setApplying(false);
        }
    };

    const handleActualizarConRespuesta = async () => {
        if (!respuesta.trim()) {
            toast.error('Escribe tu respuesta antes de actualizar la propuesta.');
            return;
        }
        setUpdating(true);
        try {
            const r = await draftsAPI.improveWriting(draft.id, { respuestas_docente: respuesta.trim() });
            if (r.data?.redaccion_ia) {
                onRedaccionUpdated(r.data.redaccion_ia);
                setRespuesta('');
                toast.success('Propuesta actualizada con tu información.');
            }
        } catch (e) {
            if (e?.response?.status !== 402) {
                toast.error(e?.response?.data?.detail?.mensaje || 'Error al actualizar la propuesta');
            }
        } finally {
            setUpdating(false);
        }
    };

    const handleSeguirEditando = () => {
        document.querySelector('[data-testid="draft-content"]')?.focus();
        toast.info('Tu texto original sigue intacto. Continúa editándolo libremente en "Mi tarea".');
    };

    return (
        <Card data-testid="redaccion-ia-panel">
            <CardHeader>
                <CardTitle className="font-heading text-lg">Asistente de Redacción</CardTitle>
                <CardDescription>
                    A partir de tu propio texto, la IA te propone dos formas de fortalecer la redacción
                    según los criterios de la rúbrica correspondiente.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {!redaccion ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Sparkles className="h-10 w-10 text-slate-300" />
                        <p className="mt-4 text-slate-500 text-sm max-w-sm">
                            Escribe tu tarea en <strong>“Mi tarea”</strong> y luego haz clic en
                            <strong> “✨ Mejorar mi redacción con IA”</strong> para recibir dos propuestas
                            orientadas a los criterios de la rúbrica.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Preguntas cuando falta información */}
                        {preguntas.length > 0 && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="redaccion-preguntas">
                                <div className="flex items-start gap-2">
                                    <HelpCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-amber-900 text-sm">
                                            Para fortalecer esta respuesta necesito información que no aparece en tu texto.
                                        </p>
                                        <ul className="mt-2 space-y-1.5">
                                            {preguntas.map((q, i) => (
                                                <li key={i} className="text-sm text-amber-900 flex items-start gap-1.5">
                                                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                                                    {q}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                <Textarea
                                    value={respuesta}
                                    onChange={(e) => setRespuesta(e.target.value)}
                                    placeholder="Escribe aquí tu respuesta con tu experiencia, evidencias o decisiones…"
                                    className="min-h-[90px] bg-white text-sm"
                                    data-testid="redaccion-respuesta-docente"
                                />
                                <Button
                                    onClick={handleActualizarConRespuesta}
                                    disabled={updating}
                                    size="sm"
                                    className="gap-2 bg-amber-600 hover:bg-amber-700"
                                    data-testid="redaccion-actualizar-btn"
                                >
                                    {updating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PenLine className="h-3.5 w-3.5" />}
                                    Actualizar propuesta con mi respuesta
                                </Button>
                            </div>
                        )}

                        {/* Tabs Competente / Destacada */}
                        <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-1 text-sm gap-1" data-testid="redaccion-tabs">
                            <button
                                onClick={() => setTab('competente')}
                                className={`flex-1 rounded-md px-3 py-2 font-semibold transition-colors ${tab === 'competente' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                data-testid="redaccion-tab-competente"
                            >
                                Versión Competente
                            </button>
                            <button
                                onClick={() => setTab('destacada')}
                                className={`flex-1 rounded-md px-3 py-2 font-semibold transition-colors ${tab === 'destacada' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                data-testid="redaccion-tab-destacada"
                            >
                                Versión Destacada
                            </button>
                        </div>

                        {/* Contenido de la pestaña activa */}
                        <div className="space-y-3" data-testid={`redaccion-contenido-${tab}`}>
                            <div>
                                <h4 className={`font-heading text-base font-bold ${tab === 'competente' ? 'text-sky-800' : 'text-emerald-800'}`}>
                                    {tab === 'competente' ? 'Versión Competente' : 'Versión Destacada'}
                                </h4>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {tab === 'competente'
                                        ? 'Esta propuesta reorganiza y fortalece tu redacción buscando responder adecuadamente a los criterios asociados al nivel Competente.'
                                        : 'Esta propuesta profundiza y fortalece tu redacción tomando como referencia los criterios asociados al nivel Destacado.'}
                                </p>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap max-h-[420px] overflow-y-auto" data-testid="redaccion-propuesta-texto">
                                {textoActual || <span className="text-slate-400 italic">Sin propuesta generada para esta versión.</span>}
                            </div>

                            {/* Decisión del docente */}
                            {confirmTarget === tab ? (
                                <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3 space-y-2" data-testid="redaccion-confirm-usar">
                                    <p className="text-xs text-indigo-900">
                                        Esta propuesta reemplazará el contenido actual de “Mi tarea”.
                                        Podrás continuar editándola libremente.
                                    </p>
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setConfirmTarget(null)} data-testid="redaccion-cancelar-btn">
                                            Cancelar
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="bg-indigo-600 hover:bg-indigo-700"
                                            onClick={() => handleUsarVersion(tab)}
                                            disabled={applying}
                                            data-testid="redaccion-confirmar-usar-btn"
                                        >
                                            {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                            Usar esta versión
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline" onClick={() => handleCopiar(tab)} className="gap-1.5" data-testid="redaccion-copiar-btn">
                                        {copied === tab ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                                        {copied === tab ? 'Copiada' : 'Copiar versión'}
                                    </Button>
                                    <Button size="sm" onClick={() => setConfirmTarget(tab)} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700" data-testid="redaccion-usar-btn">
                                        <PenLine className="h-3.5 w-3.5" />
                                        Usar como nuevo borrador
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={handleSeguirEditando} className="text-slate-600" data-testid="redaccion-seguir-editando-btn">
                                        Seguir editando
                                    </Button>
                                </div>
                            )}

                            <p className="text-[11px] text-slate-500 italic border-t border-slate-100 pt-2">
                                Esta propuesta es una guía de redacción. Tú decides qué conservar, modificar o descartar.
                                La versión final debe representar fielmente tu experiencia, evidencias y juicio profesional.
                            </p>
                        </div>
                    </>
                )}

                {/* Historial de versiones */}
                {historial.length > 0 && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="redaccion-historial">
                        <h4 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                            <History className="h-4 w-4" /> Historial de versiones
                        </h4>
                        <ul className="space-y-1.5">
                            {historial.map((v, i) => (
                                <li key={i} className="flex items-center justify-between gap-2 rounded-md bg-white border border-slate-200 px-2.5 py-1.5 text-xs" data-testid={`redaccion-version-${i}`}>
                                    <div className="min-w-0">
                                        <span className="font-semibold text-slate-800">
                                            {i === historial.length - 1 ? 'Borrador original' : 'Versión anterior'}
                                        </span>
                                        <span className="text-slate-400"> · {fmtFechaHora(v.saved_at)}</span>
                                        <span className="block text-[10px] text-slate-500 truncate">
                                            reemplazada por: {FUENTE_LABEL[v.reemplazado_por] || v.reemplazado_por || 'IA'}
                                        </span>
                                    </div>
                                    {confirmTarget === `hist-${i}` ? (
                                        <div className="flex gap-1 shrink-0">
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setConfirmTarget(null)}>
                                                Cancelar
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="h-7 px-2 text-xs bg-slate-900 hover:bg-slate-800"
                                                disabled={applying}
                                                onClick={() => handleRestaurar(v)}
                                                data-testid={`redaccion-restaurar-confirm-${i}`}
                                            >
                                                {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Restaurar'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 px-2 text-xs gap-1 shrink-0"
                                            onClick={() => setConfirmTarget(`hist-${i}`)}
                                            data-testid={`redaccion-restaurar-${i}`}
                                        >
                                            <RotateCcw className="h-3 w-3" /> Restaurar
                                        </Button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
